/**
 * Configure OAuth Modal
 * Admin-only modal for configuring OAuth credentials for integration providers
 */
import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useToast } from '../hooks/use-toast';

import {
  ArrowSquareOut,
  Check,
  CircleNotch,
  Copy,
  Info,
  Key,
  Shield,
  Trash,
  Warning
} from '@phosphor-icons/react';
const API_URL = BACKEND_URL;

// Provider-specific configuration fields with enhanced helper text
const PROVIDER_FIELDS = {
  microsoft_365: {
    name: 'Microsoft 365',
    color: 'bg-blue-500',
    docsUrl: 'https://docs.microsoft.com/en-us/azure/active-directory/develop/quickstart-register-app',
    fields: [
      { 
        key: 'client_id', 
        label: 'Application (Client) ID', 
        required: true, 
        type: 'text', 
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
        helperText: 'Found in Azure Portal → App Registration → Overview'
      },
      { 
        key: 'client_secret', 
        label: 'Client Secret', 
        required: true, 
        type: 'password', 
        placeholder: 'Enter client secret',
        helperText: 'Create under Certificates & Secrets → New client secret'
      },
      { 
        key: 'tenant_id', 
        label: 'Directory (Tenant) ID', 
        required: false, 
        type: 'text', 
        placeholder: 'common',
        helperText: 'Optional — use "common" for multi-tenant apps, or specific tenant ID for single-tenant'
      }
    ],
    instructions: [
      'Go to Azure Portal → Azure Active Directory → App Registrations',
      'Create a new registration or select existing',
      'Copy Application (Client) ID from the Overview page',
      'Under Certificates & Secrets, create a new client secret',
      'Add the Redirect URI shown below to your app\'s redirect URIs'
    ]
  },
  google_workspace: {
    name: 'Google Workspace',
    color: 'bg-red-500',
    docsUrl: 'https://console.cloud.google.com/apis/credentials',
    fields: [
      { 
        key: 'client_id', 
        label: 'Client ID', 
        required: true, 
        type: 'text', 
        placeholder: 'xxxxx.apps.googleusercontent.com',
        helperText: 'Must end with .apps.googleusercontent.com'
      },
      { 
        key: 'client_secret', 
        label: 'Client Secret', 
        required: true, 
        type: 'password', 
        placeholder: 'Enter client secret',
        helperText: 'Found in OAuth 2.0 Client ID details'
      }
    ],
    instructions: [
      'Go to Google Cloud Console → APIs & Services → Credentials',
      'Create OAuth 2.0 Client ID (select "Web application")',
      'Copy Client ID and Client Secret',
      'Add the Redirect URI shown below to authorized redirect URIs'
    ]
  },
  salesforce: {
    name: 'Salesforce',
    color: 'bg-sky-500',
    docsUrl: 'https://help.salesforce.com/s/articleView?id=sf.connected_app_create.htm',
    fields: [
      { 
        key: 'client_id', 
        label: 'Consumer Key', 
        required: true, 
        type: 'text', 
        placeholder: 'Enter consumer key',
        helperText: 'Found in Connected App details after creation'
      },
      { 
        key: 'client_secret', 
        label: 'Consumer Secret', 
        required: true, 
        type: 'password', 
        placeholder: 'Enter consumer secret',
        helperText: 'Click "Click to reveal" in Connected App details'
      },
      { 
        key: 'instance_url', 
        label: 'Instance URL', 
        required: false, 
        type: 'text', 
        placeholder: 'https://yourcompany.salesforce.com',
        helperText: 'Optional — Production: yourcompany.salesforce.com, Sandbox: yourcompany--sandbox.sandbox.my.salesforce.com',
        validate: (value) => {
          if (!value) return null;
          if (!value.startsWith('https://')) return 'Must start with https://';
          if (!value.includes('salesforce.com') && !value.includes('force.com')) return 'Must be a Salesforce domain';
          return null;
        }
      }
    ],
    instructions: [
      'Go to Setup → Apps → App Manager → New Connected App',
      'Enable OAuth Settings and select required scopes',
      'Add the Redirect URI shown below to callback URLs',
      'After saving, wait 2-10 minutes for changes to propagate',
      'Copy Consumer Key and Consumer Secret from the app details'
    ]
  },
  xero: {
    name: 'Xero',
    color: 'bg-teal-500',
    docsUrl: 'https://developer.xero.com/documentation/getting-started-guide/',
    fields: [
      { 
        key: 'client_id', 
        label: 'Client ID', 
        required: true, 
        type: 'text', 
        placeholder: 'Enter client ID',
        helperText: 'Found in Xero Developer Portal → My Apps'
      },
      { 
        key: 'client_secret', 
        label: 'Client Secret', 
        required: true, 
        type: 'password', 
        placeholder: 'Enter client secret',
        helperText: 'Generate a new secret if you don\'t have the original'
      }
    ],
    instructions: [
      'Go to Xero Developer Portal → My Apps',
      'Create a new app (Web App type) or select existing',
      'Copy Client ID and generate Client Secret',
      'Add the Redirect URI shown below to redirect URIs'
    ]
  },
  square: {
    name: 'Square',
    color: 'bg-black',
    docsUrl: 'https://developer.squareup.com/docs/oauth-api/overview',
    fields: [
      { 
        key: 'client_id', 
        label: 'Application ID', 
        required: true, 
        type: 'text', 
        placeholder: 'sq0idp-xxxxxxxxxxxxxxxx',
        helperText: 'Found in Square Developer Dashboard → Application → OAuth'
      },
      { 
        key: 'client_secret', 
        label: 'Application Secret', 
        required: true, 
        type: 'password', 
        placeholder: 'Enter application secret',
        helperText: 'Found in Square Developer Dashboard → Application → OAuth'
      },
      {
        key: 'use_sandbox',
        label: 'Use Sandbox (Testing)',
        required: false,
        type: 'checkbox',
        helperText: 'Enable for testing with sandbox credentials'
      }
    ],
    instructions: [
      'Go to Square Developer Dashboard → Applications',
      'Create a new application or select existing',
      'Navigate to OAuth tab',
      'Copy Application ID and Application Secret',
      'Add the Redirect URI shown below to redirect URLs',
      'Ensure your Square account has Appointments enabled'
    ]
  },
  calendly: {
    name: 'Calendly',
    color: 'bg-blue-600',
    docsUrl: 'https://developer.calendly.com/create-a-developer-account',
    fields: [
      { 
        key: 'client_id', 
        label: 'Client ID', 
        required: true, 
        type: 'text', 
        placeholder: 'Enter client ID',
        helperText: 'Found in Calendly Developer Portal → OAuth Applications'
      },
      { 
        key: 'client_secret', 
        label: 'Client Secret', 
        required: true, 
        type: 'password', 
        placeholder: 'Enter client secret',
        helperText: 'Only shown once when creating the app — save it securely'
      }
    ],
    instructions: [
      'Go to Calendly Developer Portal and create an account',
      'Create a new OAuth application',
      'Choose Web application type',
      'Copy Client ID and Client Secret (secret shown only once)',
      'Add the Redirect URI shown below to redirect URIs'
    ]
  }
};

export default function ConfigureOAuthModal({ 
  open, 
  onOpenChange, 
  provider, 
  redirectUri,
  onConfigured,
  organizationId,
  isConfigured = false  // New prop to indicate if OAuth is already configured
}) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { toast } = useToast();

  const providerConfig = PROVIDER_FIELDS[provider] || {};

  // Helper to get headers with organization context
  const getOrgHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (organizationId) {
      headers['X-Organization-Id'] = organizationId;
    }
    return headers;
  };

  const handleCopyRedirectUri = () => {
    navigator.clipboard.writeText(redirectUri);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeleteConfig = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/org/integrations/${provider}/oauth-config`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getOrgHeaders()
      });

      if (response.ok) {
        toast({
          title: 'Configuration Removed',
          description: `OAuth credentials for ${providerConfig.name} have been deleted. You can now reconfigure with new credentials.`,
          className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
        });
        setFormData({});
        setShowDeleteConfirm(false);
        onOpenChange(false);
        if (onConfigured) onConfigured();
      } else {
        const data = await response.json();
        toast({
          title: 'Delete Failed',
          description: data.detail || 'Failed to delete OAuth configuration',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete configuration',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSave = async () => {
    // Validate required fields
    const missingFields = (providerConfig.fields || [])
      .filter(f => f.required && !formData[f.key])
      .map(f => f.label);

    if (missingFields.length > 0) {
      toast({
        title: 'Missing Required Fields',
        description: `Please fill in: ${missingFields.join(', ')}`,
        variant: 'destructive'
      });
      return;
    }

    // Check validation errors
    const validationErrors = (providerConfig.fields || [])
      .filter(f => f.validate && formData[f.key] && f.validate(formData[f.key]))
      .map(f => ({ field: f.label, error: f.validate(formData[f.key]) }));

    if (validationErrors.length > 0) {
      toast({
        title: 'Validation Error',
        description: validationErrors[0].error,
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/org/integrations/${provider}/oauth-config`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(),
        body: JSON.stringify({
          ...formData,
          redirect_uri: redirectUri
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Configuration Saved',
          description: `OAuth credentials for ${providerConfig.name} have been securely stored.`,
          className: 'bg-[#1a1a1a] border-[#14F195]/30 text-white'
        });
        setFormData({});
        onOpenChange(false);
        if (onConfigured) onConfigured();
      } else {
        toast({
          title: 'Configuration Failed',
          description: data.detail || 'Failed to save OAuth configuration',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save configuration',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    onOpenChange(false);
  };

  if (!provider || !providerConfig.name) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#111111] border-[rgba(255,255,255,0.1)] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${providerConfig.color}`}>
              <Key className="w-5 h-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-white">Configure OAuth – {providerConfig.name}</DialogTitle>
              <DialogDescription className="text-gray-400 text-sm mt-1">
                Enter your OAuth credentials to enable this integration.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Explanation */}
          <div className="p-3 bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.2)] rounded-lg">
            <p className="text-sm text-gray-300">
              OAuth credentials tell Man & Machine how to securely communicate with your {providerConfig.name} account. 
              This only needs to be configured once.
            </p>
          </div>

          {/* Redirect URI */}
          <div>
            <Label className="text-gray-300 text-sm flex items-center gap-2">
              <Info className="w-3 h-3" />
              Redirect URI (add this to your app)
            </Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={redirectUri || ''}
                readOnly
                className="bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.1)] text-gray-400 text-sm font-mono"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyRedirectUri}
                className="border-[rgba(255,255,255,0.1)] text-gray-300 px-3"
              >
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Credential Fields */}
          {(providerConfig.fields || []).map((field) => {
            const validationError = field.validate ? field.validate(formData[field.key]) : null;
            return (
              <div key={field.key}>
                <Label className="text-gray-300 text-sm flex items-center gap-2">
                  {field.label} 
                  {field.required ? (
                    <span className="text-red-400">*</span>
                  ) : (
                    <span className="text-gray-500 text-xs font-normal">(optional)</span>
                  )}
                </Label>
                <Input
                  type={field.type}
                  value={formData[field.key] || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className={`bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white mt-1 ${
                    validationError ? 'border-red-500/50' : ''
                  }`}
                />
                {field.helperText && !validationError && (
                  <p className="text-[11px] text-gray-500 mt-1">{field.helperText}</p>
                )}
                {validationError && (
                  <p className="text-[11px] text-red-400 mt-1">{validationError}</p>
                )}
              </div>
            );
          })}

          {/* Setup Instructions */}
          <details className="group">
            <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-300 flex items-center gap-2">
              <ArrowSquareOut className="w-3 h-3" />
              How to get these credentials
            </summary>
            <div className="mt-3 p-3 bg-[rgba(255,255,255,0.02)] rounded-lg">
              <ol className="space-y-2 text-sm text-gray-400">
                {(providerConfig.instructions || []).map((step, idx) => (
                  <li key={idx} className="flex gap-2">
                    <span className="text-[#6366F1] font-medium">{idx + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              {providerConfig.docsUrl && (
                <a 
                  href={providerConfig.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-sm text-[#6366F1] hover:underline"
                >
                  View full documentation
                  <ArrowSquareOut className="w-3 h-3" />
                </a>
              )}
            </div>
          </details>

          {/* Security Notice */}
          <div className="flex items-start gap-2 p-3 bg-[rgba(20,241,149,0.05)] border border-[rgba(20,241,149,0.2)] rounded-lg">
            <Shield className="w-4 h-4 text-[#14F195] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-400">
              Your credentials are encrypted at rest and never exposed after saving. 
              Only admins can configure OAuth settings.
            </p>
          </div>

          {/* Reset Option - only shown when already configured */}
          {isConfigured && (
            <div className="flex items-start gap-2 p-3 bg-[rgba(239,68,68,0.05)] border border-[rgba(239,68,68,0.2)] rounded-lg">
              <Warning className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-2">
                  This integration is already configured. To use new credentials, you can reset the current configuration.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-7 text-xs"
                >
                  <Trash className="w-3 h-3 mr-1" />
                  Reset Configuration
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-[rgba(255,255,255,0.1)] text-gray-300"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#6366F1] hover:bg-[#5558E3]"
          >
            {saving ? (
              <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Key className="w-4 h-4 mr-2" />
            )}
            {isConfigured ? 'Update Configuration' : 'Save Configuration'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Trash Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-[#111111] border-[rgba(255,255,255,0.1)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Reset OAuth Configuration?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will remove the stored OAuth credentials for {providerConfig.name} and disconnect any active connections. 
              You will need to reconfigure the integration with new credentials.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[rgba(255,255,255,0.1)] text-gray-300 hover:bg-[rgba(255,255,255,0.05)]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfig}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? (
                <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash className="w-4 h-4 mr-2" />
              )}
              Reset Configuration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
